/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
/**
 * ControlsPanel.jsx
 *
 * Main control panel for the text embedding visualization app.
 * Provides UI for text input, parameter adjustment (including coloring, environment, and fog), and actions (generate, save, open).
 * Uses Material UI for layout and controls.
 *
 * Copyright (c) 2023-2025, text-embedding-visualization authors.
 * Licensed under the MIT License.
 * Author: [Your Name]
 * Date: [Update as needed]
 */

/**
 * ControlsPanel
 *
 * @component
 * @param {Object} props
 * @param {string} props.inputText - Current input text.
 * @param {Array} props.tokens - Array of token objects.
 * @param {Array} props.chunks - Array of chunk objects (windows).
 * @param {boolean} props.showTokenIds - Whether to display token IDs.
 * @param {function} props.onInputTextChange - Callback for text change.
 * @param {function} props.onToggleShowTokenIds - Toggle showTokenIds.
 * @param {string} props.textPanel_tokenColorMode - Token coloring mode for input text panel.
 * @param {function} props.onChangeTextPanel_tokenColorMode - Callback for input text panel coloring mode change.
 * @param {string} props.displayParam_tokenColorMode - Token coloring mode for display parameters.
 * @param {function} props.onChangeDisplayParam_tokenColorMode - Callback for display parameter coloring mode change.
 * @param {string} props.displayParam_environmentColorMode - Environment (background) color mode for 3D scene.
 * @param {function} props.onChangeDisplayParam_environmentColorMode - Callback for environment color mode change.
 * @param {string} props.displayParam_fogColorMode - Fog color mode for 3D scene.
 * @param {function} props.onChangeDisplayParam_fogColorMode - Callback for fog color mode change.
 * @param {number} props.windowSize - Current window size value.
 * @param {function} props.onWindowSizeChange - Callback for window size change.
 * @param {number} props.overlap - Current overlap value.
 * @param {function} props.onOverlapChange - Callback for overlap change.
 * @param {number} props.embeddingDimensions - Embedding dimension value.
 * @param {function} props.onEmbeddingDimensionsChange - Callback for dimension change.
 * @param {boolean} props.showLabels - Whether to show first-word labels.
 * @param {function} props.onToggleShowLabels - Toggle showLabels.
 * @param {function} props.onGenerateVisualization - Callback for processing action.
 * @param {boolean} props.isLoading - Processing/loading state.
 * @param {boolean} props.isTokenizing - Whether tokenization is in progress.
 * @param {boolean} props.isChunking - Whether chunking is in progress.
 * @param {boolean} [props.canGenerate] - Whether Generate Visualization is currently allowed.
 * @param {function} props.setIsChunking - Setter for chunking state.
 * @param {number} props.textSize - Text size for visualization.
 * @param {function} props.onTextSizeChange - Callback for text size change.
 * @param {number} props.dotSize - Dot size for visualization.
 * @param {function} props.onDotSizeChange - Callback for dot size change.
 * @param {number} props.labelOffset - Vertical offset for label display.
 * @param {function} props.onLabelOffsetChange - Callback for label offset change.
 * @param {function} props.getTokenColor - Function to get color for a token id.
 * @param {function} props.getSequenceColor - Function to get color for a token index in sequence.
 * @param {boolean} props.readyToSave - Whether the Save button should be enabled.
 * @param {function} props.handleSave - Callback for when the Save button is pressed.
 * @param {function} [props.onOpenTevJson] - Callback when a TEV JSON file is loaded.
 * @param {string} props.showPathMode - Path display mode (curve, lines, none).
 * @param {function} props.onChangeShowPathMode - Handler for path mode change.
 * @param {string} props.pointStyle - Point style (circle, ring, cloud).
 * @param {function} props.onChangePointStyle - Handler for point style change.
 * @param {boolean} props.showHelpers - Whether to show 3D helpers.
 * @param {function} props.onToggleShowHelpers - Handler for toggling helpers.
 * @param {Array} [props.poems] - Array of poems for selection.
 * @param {number|string} [props.selectedPoemIndex] - Selected poem index.
 * @param {function} [props.onSelectPoem] - Handler for poem selection.
 * @param {function} [props.onEnterVR] - Handler for entering VR mode.
 */

/**
 * @typedef {import('../types/chunk').Chunk} Chunk
 * @typedef {import('../types/token').Token} Token
 * @typedef {import('../types/embedding').Embedding} Embedding
 * @typedef {import('../types/pca').PCA} PCA
 */

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Tooltip from '@mui/material/Tooltip';
import DisplayParametersPanel from './DisplayParametersPanel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import LinkIcon from '@mui/icons-material/Link';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import SessionBrowserPanel from './SessionBrowserPanel';

/**
 * ControlsPanel component definition
 */
export default function ControlsPanel(props) {
  // Destructure all props for clarity
  // Point style and showPathMode are now controlled by parent (TextEmbeddingVisualizer)
  const {
    displayParam_tokenColorMode,
    onChangeDisplayParam_tokenColorMode,
    displayParam_labelColorMode,
    onChangeDisplayParam_labelColorMode,
    displayParam_environmentColorMode,
    onChangeDisplayParam_environmentColorMode,
    displayParam_fogColorMode,
    onChangeDisplayParam_fogColorMode,
    showLabels,
    onToggleShowLabels,
    isLoading,
    textSize,
    onTextSizeChange,
    dotSize,
    onDotSizeChange,
    labelOffset,
    onLabelOffsetChange,
    showHelpers,
    onToggleShowHelpers,
    selectedSessionId,
    onSelectSession,
    getTokenColor,
    getSequenceColor,
    showPathMode,
    onChangeShowPathMode,
    pointStyle,
    onChangePointStyle,
    handleExportSession,
  } = props;

  // State for collapsible sections in the panel
  const [displayParamsOpen, setDisplayParamsOpen] = useState(false);
  const [sessionBrowserOpen, setSessionBrowserOpen] = useState(true);

  // Detect WebXR support for the AR/VR button tooltip and disabled state.
  // On mobile browsers without WebXR (e.g. iOS Safari) the button is disabled with an explanatory tooltip.
  // On Android Chrome the button triggers immersive-ar (an AR overlay of the 3D scene).
  const [xrSupported, setXrSupported] = useState<boolean | null>(null);
  useEffect(() => {
    type XRNavigator = Navigator & { xr?: { isSessionSupported: (mode: string) => Promise<boolean> } };
    const xrNavigator = navigator as XRNavigator;
    if (typeof navigator !== 'undefined' && xrNavigator.xr) {
      xrNavigator.xr.isSessionSupported('immersive-ar')
        .then((supported: boolean) => setXrSupported(supported))
        .catch(() => setXrSupported(false));
    } else {
      setXrSupported(false);
    }
  }, []);

  const xrButtonDisabled = !props.onEnterVR || xrSupported === false;
  const xrTooltip = xrSupported === false
    ? 'WebXR AR not supported in this browser. Try Chrome on Android.'
    : xrSupported === true
      ? 'Enter AR mode — overlays the semantic space on your camera (WebXR immersive-ar)'
      : 'Enter VR/AR mode (WebXR)';

  const handleAdminClick = () => {
    window.location.href = '/admin';
  };

  // Main panel container
  return (
    <Paper
      elevation={6}
      sx={{
        top: 0,
        left: 0,
        zIndex: 10,
        p: 1,
        borderRadius: 2,
        maxWidth: 400,
        minWidth: 320,
        fontSize: '0.90rem',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)', // Full height minus the top controls panel and pca matrix panel
        overflow: 'auto',
      }}
    >
      {/* Title and VR/AR button */}
      <Box display="flex" alignItems="center" mb={.5}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/semantic-cartographer-logo-bw-4.svg`}
          alt="Semantic Cartographer logo"
          sx={{ width: 28, height: 28, mr: 1, flexShrink: 0 }}
        />
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem', flex: 1 }}>
          Semantic Cartographer
        </Typography>
        {/* AR/VR mode button (WebXR) */}
        <Tooltip title={xrTooltip} arrow>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={props.onEnterVR}
              aria-label="Enter AR/VR"
              sx={{ ml: 1 }}
              disabled={xrButtonDisabled}
            >
              <ViewInArIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Instructions */}
      <Box
        mb={.5}
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Browse source texts with saved visualization sessions. Selecting a session loads and renders it immediately.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Embedding dimensionality is defined by each saved session and is no longer a separate control.
        </Typography>
      </Box>

      {/* Session Browser Panel */}
      <Box
        mb={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          padding: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Session browser header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Browse Sessions{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 400,
                fontSize: '0.75em',
                color: 'text.secondary',
                display: 'inline',
              }}
            >
              [auto-load]
            </Box>
          </Typography>
          <Box display="flex" alignItems="center">
              <Tooltip
              title="Select a source text with available sessions, then click a session to render immediately."
              placement="top"
              arrow
            >
              <HelpOutlineIcon 
                fontSize="small" 
                sx={{ color: 'text.secondary', cursor: 'pointer', ml: 1 }} />
            </Tooltip>
            {/* Expand/collapse button for poem selector */}
            <IconButton
              size="small"
              onClick={() => setSessionBrowserOpen((open) => !open)}
              aria-label={sessionBrowserOpen ? "Collapse" : "Expand"}
              sx={{
                transform: sessionBrowserOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
                ml: 1,
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>
        <Collapse in={sessionBrowserOpen}>
          <SessionBrowserPanel
            selectedSessionId={selectedSessionId}
            onSelectSession={onSelectSession}
            isLoading={isLoading}
          />
        </Collapse>
      </Box>

      {/* Main controls area (scrollable) */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Display parameters panel (color, point style, path, etc.) */}
        <DisplayParametersPanel
          displayParamsOpen={displayParamsOpen}
          setDisplayParamsOpen={setDisplayParamsOpen}
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          onChangeDisplayParam_tokenColorMode={onChangeDisplayParam_tokenColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          onChangeDisplayParam_labelColorMode={onChangeDisplayParam_labelColorMode}
          showLabels={showLabels}
          onToggleShowLabels={onToggleShowLabels}
          showPathMode={showPathMode}
          onChangeShowPathMode={onChangeShowPathMode}
          pointStyle={pointStyle}
          onChangePointStyle={onChangePointStyle}
          showHelpers={showHelpers}
          onToggleShowHelpers={onToggleShowHelpers}
          textSize={textSize}
          onTextSizeChange={onTextSizeChange}
          dotSize={dotSize}
          onDotSizeChange={onDotSizeChange}
          labelOffset={labelOffset}
          onLabelOffsetChange={onLabelOffsetChange}
          displayParam_fogColorMode={displayParam_fogColorMode}
          onChangeDisplayParam_fogColorMode={onChangeDisplayParam_fogColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          onChangeDisplayParam_environmentColorMode={onChangeDisplayParam_environmentColorMode}
        />

      </Box>

      {/* Share Session and Admin buttons */}
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          fullWidth
          startIcon={<AdminPanelSettingsIcon />}
          onClick={handleAdminClick}
          sx={{ py: 0.5 }}
        >
          Admin
        </Button>
        {handleExportSession && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            startIcon={<LinkIcon />}
            onClick={handleExportSession}
            sx={{ py: 0.5 }}
          >
            Share Session
          </Button>
        )}
      </Box>
    </Paper>
  );
}
